import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import JalaliRangePicker from 'components/jalaali-range-picker/JalaliRangePicker';
import JalaliDatePicker from 'components/jalaali-date-picker/JalaliDatePicker';
import moment from 'moment';
import { usePlatform } from 'hooks';
import { mapLanguageToCalendar, getEnv } from 'utils';
import {
  applyFiltersToApiRequest,
  applyFiltersToAllPlatforms,
  setCurrentDate,
  setDefaultCurrentDateSettings,
  updateWebsiteSettings,
  updateChartsTimeGap,
  setPlatformCurrentPageNumber,
} from 'store';
import QueryFilter from './query-filter/QueryFilter';

const HeaderFilter = ({ showQueryFilter = true }) => {
  const displayDatePicker = useSelector(
    (state) => state.pageReducer.displayDatePicker
  );
  const displayDateTimePicker = useSelector(
    (state) => state.pageReducer.displayDateTimePicker
  );
  const displayQuery = useSelector((state) => state.pageReducer.displayQuery);
  const dateTimePickerMinDate = useSelector(
    (state) => state.pageReducer.dateTimePickerMinDate
  );
  const dateTimePickerMaxDate = useSelector(
    (state) => state.pageReducer.dateTimePickerMaxDate
  );
  const currentDate = useSelector((state) => state.pageReducer.currentDate);
  const defaultDateTimeSelectBox = useSelector(
    (state) => state.settingsReducer.filters.public.defaultDateTimeSelectBox
  );
  const dispatch = useDispatch();
  const { getSettingsDefaultPlatform } = usePlatform();
  const { platform: activePlatform = getSettingsDefaultPlatform() } =
    useParams();

  /**
   * ذخیره تغییرات تقویم از نوع بازه زمانی
   * @param {*} dateTime
   */
  const handelRangeChange = useCallback(
    (dateTime) => {
      dispatch(
        setPlatformCurrentPageNumber({
          platform: activePlatform,
          pageNumber: 1,
        })
      );

      const fromDate = dateTime[0];
      const toDate = dateTime[1];
      const timeDuration = dateTime[2];
      if (timeDuration) {
        dispatch(
          setCurrentDate({
            fromDate: undefined,
            toDate: undefined,
            timeDuration: timeDuration,
          })
        );
      } else {
        dispatch(
          setCurrentDate({
            fromDate,
            toDate,
            timeDuration: undefined,
          })
        );
      }
      dispatch(
        updateChartsTimeGap({
          currentDate: {
            fromDate,
            toDate,
            timeDuration,
          },
        })
      );
      if (!window.location.pathname.includes('contents')) {
        dispatch(applyFiltersToApiRequest());
      }
      dispatch(applyFiltersToAllPlatforms());
      if (
        defaultDateTimeSelectBox.selectedTimeDuration ===
        'withoutDefaultDateTime'
      ) {
        let defaultCurrentDateSettings = {};
        if (fromDate && toDate) {
          defaultCurrentDateSettings = {
            selectedTimeDuration: 'withoutDefaultDateTime',
            timeDuration: undefined,
            fromDate,
            toDate,
            hasDefaultDateTime: false,
          };
        } else if (timeDuration) {
          defaultCurrentDateSettings = {
            selectedTimeDuration: 'withoutDefaultDateTime',
            timeDuration: ['3m', '6m', '12m'].includes(timeDuration)
              ? '1m'
              : timeDuration,
            fromDate: undefined,
            toDate: undefined,
            hasDefaultDateTime: false,
          };
        }
        dispatch(setDefaultCurrentDateSettings(defaultCurrentDateSettings));
        dispatch(updateWebsiteSettings({ shouldApplyToOtherReducers: false }));
      }
    },
    [activePlatform, defaultDateTimeSelectBox.selectedTimeDuration, dispatch]
  );

  /**
   * ذخیره تغییرات تقویم از نوع تاریخ
   * @param {*} dateTime
   */
  const handelDateChange = useCallback(
    (dateTime) => {
      dispatch(
        setPlatformCurrentPageNumber({
          platform: activePlatform,
          pageNumber: 1,
        })
      );
      dispatch(
        setCurrentDate({
          date: moment(dateTime).format('YYYY-MM-DD'),
        })
      );
      dispatch(applyFiltersToApiRequest());
    },
    [activePlatform, dispatch]
  );

  // ساخت پراپرتی های داینامیک برای تقویم بازه زمانی

  const currentDateTime = useMemo(() => {
    const dateTime = {
      minDate: dateTimePickerMinDate || null,
      maxDate: dateTimePickerMaxDate || null,
    };

    if (currentDate.fromDate && currentDate.toDate) {
      // به صورت پیشفرض تاریخ ابتدا و انتها ست میشود
      dateTime['dateTime'] = {
        fromDate: currentDate.fromDate,
        toDate: currentDate.toDate,
      };
    } else {
      // به صورت پیشفرض بازه زمانی ست میشود
      dateTime['duration'] = currentDate.timeDuration;
    }

    return dateTime;
  }, [
    dateTimePickerMinDate,
    dateTimePickerMaxDate,
    currentDate.timeDuration,
    currentDate.fromDate,
    currentDate.toDate,
  ]);

  return (
    <div className='header-filter flex padding'>
      <div className='header-filter-wrapper flex flex-1'>
        {/* فیلتر کلمات کلیدی */}
        {displayQuery && showQueryFilter && <QueryFilter />}

        {/* اگر درتنظیمات کانتکست گفته شده بود تقویم از نوع بازه زمانی */}
        {displayDateTimePicker && (
          <JalaliRangePicker
            onChange={handelRangeChange}
            {...currentDateTime}
            data-testid='header-JalaliRangePicker'
            selectedCalendar={
              mapLanguageToCalendar[getEnv('REACT_APP_LANGUAGE')]
            }
          />
        )}

        {/* اگر در تنظیمات کانتکست گفته شده بود تقویم از نوع تاریخ */}
        {displayDatePicker && (
          <JalaliDatePicker
            onChange={handelDateChange}
            date={currentDate.date || null}
            data-testid='header-JalaliDatePicker'
            hasFooter
            selectedCalendar={
              mapLanguageToCalendar[getEnv('REACT_APP_LANGUAGE')]
            }
          />
        )}
      </div>
    </div>
  );
};

HeaderFilter.propTypes = {
  showQueryFilter: PropTypes.bool,
};

export default HeaderFilter;
